import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useSearchParams } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { ClerkProvider, useAuth, useUser } from "@clerk/clerk-react";
import Sidebar from "./components/Sidebar";
import ChatWindow from "./components/ChatWindow";
import Inspector from "./components/Inspector";
import ProviderModal from "./components/ProviderModal";
import NewChatModal from "./components/NewChatModal";
import MarketplacePage from "./components/Marketplace";
import AgentPage from "./components/AgentPage";
import EarningsPage from "./components/EarningsPage";
import LandingPage from "./LandingPage.jsx";
import PublishModal from "./components/PublishModal";
import FloatingNav from "./components/FloatingNav";
import { listProviders, createProvider, deleteProvider, listChats, createChat, deleteChat, getChatMessages, sendMessageStream, setAuthToken, getPaymentStatus } from "./lib/api";

const CLERK_KEY = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;

function ChatPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isSignedIn } = useAuth();
  const { user: clerkUser } = useUser();
  const [providers, setProviders] = useState([]);
  const [chats, setChats] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isSending, setIsSending] = useState(false);
  const [streamingMsgId, setStreamingMsgId] = useState(null);
  const [showProviderModal, setShowProviderModal] = useState(false);
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);

  const loadChats = useCallback(async () => {
    if (!isSignedIn) { setChats([]); return; }
    try { setChats((await listChats()).data); } catch {}
  }, [isSignedIn]);

  const pollPayment = useCallback(async (sessionId, attempt = 0) => {
    if (attempt >= 5) { toast.info("Payment processing..."); return; }
    try {
      const res = await getPaymentStatus(sessionId);
      if (res.data.payment_status === "paid") {
        toast.success(res.data.type === "pass" ? "Access pass activated!" : "Preview credits added!");
        loadChats();
      } else {
        setTimeout(() => pollPayment(sessionId, attempt + 1), 2000);
      }
    } catch {
      setTimeout(() => pollPayment(sessionId, attempt + 1), 2000);
    }
  }, [loadChats]);

  // Check for payment redirect
  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (sessionId && (searchParams.get("payment_success") || searchParams.get("preview_paid"))) {
      pollPayment(sessionId);
      navigate("/chat", { replace: true });
    }
    if (searchParams.get("payment_cancelled")) {
      toast.info("Payment cancelled");
      navigate("/chat", { replace: true });
    }
  }, [searchParams, navigate, pollPayment]);

  const loadProviders = useCallback(async () => { try { setProviders((await listProviders()).data); } catch {} }, []);
  const loadMessages = useCallback(async (chatId) => { try { setMessages((await getChatMessages(chatId)).data); } catch {} }, []);

  useEffect(() => { loadProviders(); loadChats(); }, [loadProviders, loadChats]);

  const handleSelectChat = async (chat) => { setActiveChat(chat); await loadMessages(chat.id); };
  const handleAddProvider = async (data) => { try { await createProvider(data); toast.success("Provider added"); await loadProviders(); setShowProviderModal(false); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const handleDeleteProvider = async (id) => { try { await deleteProvider(id); toast.success("Deleted"); await loadProviders(); await loadChats(); if (activeChat?.provider_id === id) { setActiveChat(null); setMessages([]); } } catch { toast.error("Failed"); } };
  const handleCreateChat = async (data) => { try { const res = await createChat(data); toast.success("Chat created"); await loadChats(); setActiveChat(res.data); setMessages([]); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const handleDeleteChat = async (chatId) => { try { await deleteChat(chatId); toast.success("Deleted"); await loadChats(); if (activeChat?.id === chatId) { setActiveChat(null); setMessages([]); } } catch { toast.error("Failed"); } };

  const handleSendMessage = async (text, options = {}) => {
    if (!activeChat) return;
    setIsSending(true);
    const tempUserMsgId = "user-" + Date.now();
    setMessages((prev) => [...prev, { id: tempUserMsgId, chat_id: activeChat.id, role: "user", content: text || "[Image]",
      memory_context: null, image_base64: options.image_base64 || null,
      _imagePreview: options.image_base64 ? `data:image/png;base64,${options.image_base64}` : null,
      web_search_context: options.web_search ? "pending" : null, deep_reasoning: options.deep_reasoning || null, created_at: new Date().toISOString() }]);
    const streamId = "stream-" + Date.now();
    setMessages((prev) => [...prev, { id: streamId, chat_id: activeChat.id, role: "assistant", content: "", created_at: new Date().toISOString(),
      _streaming: true, _webSearchUsed: options.web_search || false, _deepReasoning: options.deep_reasoning || false }]);
    setStreamingMsgId(streamId);
    try {
      await sendMessageStream(activeChat.id, text || "[Image attached]", options,
        (chunk) => setMessages((prev) => prev.map((m) => m.id === streamId ? { ...m, content: m.content + chunk } : m)),
        (meta) => setMessages((prev) => prev.map((m) => m.id === tempUserMsgId ? { ...m, id: meta.user_msg_id || tempUserMsgId, memory_context: meta.memory_used ? "true" : null } : m)),
        (data) => { setMessages((prev) => prev.map((m) => m.id === streamId ? { ...m, id: data.message_id || streamId, content: data.content || m.content, _streaming: false } : m)); setStreamingMsgId(null); setIsSending(false); },
        (detail) => { toast.error(detail || "Failed"); setMessages((prev) => prev.filter((m) => m.id !== streamId)); setStreamingMsgId(null); setIsSending(false); },
        (ctx) => setMessages((prev) => prev.map((m) => m.id === tempUserMsgId ? { ...m, web_search_context: ctx } : m)));
    } catch (e) { toast.error(e.message || "Failed"); setMessages((prev) => prev.filter((m) => m.id !== tempUserMsgId && m.id !== streamId)); setStreamingMsgId(null); setIsSending(false); }
  };

  const handleNewChat = () => {
    if (!isSignedIn) { toast.error("Sign in to create chats"); return; }
    if (!providers.length) { setShowProviderModal(true); toast.info("Add a provider first"); return; }
    setShowNewChatModal(true);
  };
  const currentUser = isSignedIn && clerkUser ? { user_id: clerkUser.id, email: clerkUser.primaryEmailAddress?.emailAddress, name: clerkUser.fullName } : null;

  return (
    <div className="flex h-screen overflow-hidden bg-[#080B10]">
      <Sidebar chats={chats} providers={providers} activeChat={activeChat} onSelectChat={handleSelectChat}
        onNewChat={handleNewChat} onDeleteChat={handleDeleteChat} />
      <ChatWindow chat={activeChat} messages={messages} onSendMessage={handleSendMessage} isSending={isSending} />
      <Inspector chat={activeChat} messages={messages} providers={providers} onPublish={() => setShowPublishModal(true)} user={currentUser} />
      <FloatingNav onOpenChats={() => navigate("/chat")} onOpenMarketplace={() => navigate("/marketplace")} onOpenEarnings={() => navigate("/earnings")} onOpenProviders={() => setShowProviderModal(true)} providerCount={providers.length} />
      <ProviderModal open={showProviderModal} onClose={setShowProviderModal} providers={providers} onAddProvider={handleAddProvider} onDeleteProvider={handleDeleteProvider} />
      <NewChatModal open={showNewChatModal} onClose={setShowNewChatModal} providers={providers} onCreateChat={handleCreateChat} />
      <PublishModal open={showPublishModal} onClose={setShowPublishModal} chat={activeChat} user={currentUser} />
    </div>
  );
}

function MarketplaceRoute() {
  const { isSignedIn } = useAuth();
  const { user: clerkUser } = useUser();
  const currentUser = isSignedIn && clerkUser ? { user_id: clerkUser.id, email: clerkUser.primaryEmailAddress?.emailAddress, name: clerkUser.fullName } : null;
  return <MarketplacePage user={currentUser} />;
}

function AgentRoute() {
  const { isSignedIn } = useAuth();
  const { user: clerkUser } = useUser();
  const currentUser = isSignedIn && clerkUser ? { user_id: clerkUser.id, email: clerkUser.primaryEmailAddress?.emailAddress, name: clerkUser.fullName } : null;
  return <AgentPage user={currentUser} />;
}

function EarningsRoute() {
  const { isSignedIn } = useAuth();
  const { user: clerkUser } = useUser();
  const currentUser = isSignedIn && clerkUser ? { user_id: clerkUser.id, email: clerkUser.primaryEmailAddress?.emailAddress, name: clerkUser.fullName } : null;
  return <EarningsPage user={currentUser} />;
}

function AppContent() {
  const { getToken, isSignedIn } = useAuth();

  // Global auth token sync — covers all routes
  useEffect(() => {
    const syncToken = async () => {
      if (isSignedIn) {
        const token = await getToken();
        setAuthToken(token);
      } else {
        setAuthToken(null);
      }
    };
    syncToken();
    const interval = setInterval(syncToken, 50000);
    return () => clearInterval(interval);
  }, [isSignedIn, getToken]);

  return (
    <>
      <Toaster position="top-right" theme="dark" toastOptions={{ style: { background: "#111620", border: "1px solid #273142", color: "#FFF", borderRadius: "0", fontFamily: "'IBM Plex Sans', sans-serif" } }} />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/agents" element={<AgentRoute />} />
          <Route path="/chat" element={<ChatPage />} />
          <Route path="/marketplace" element={<MarketplaceRoute />} />
          <Route path="/earnings" element={<EarningsRoute />} />
        </Routes>
      </BrowserRouter>
    </>
  );
}

function App() {
  if (!CLERK_KEY) {
    return <div className="flex h-screen items-center justify-center bg-[#080B10] text-[#FF3366] font-mono text-sm">Missing REACT_APP_CLERK_PUBLISHABLE_KEY</div>;
  }
  return (
    <ClerkProvider publishableKey={CLERK_KEY}>
      <AppContent />
    </ClerkProvider>
  );
}

export default App;
